import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { buildAboutLd } from "@/lib/page-schemas";

const TITLE = "About DrHint — Real-Time Multi-Utility Micro-SaaS Platform";
const DESC =
  "DrHint is a premium multi-utility Micro-SaaS engineered to democratize real-time automated data processing across precious metals, regional utility bills and loan optimization.";

export const Route = createFileRoute("/about-us")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "robots", content: "index,follow,max-snippet:-1" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: "https://drhint.com/about-us" },
    ],
    links: [{ rel: "canonical", href: "https://drhint.com/about-us" }],
    scripts: buildAboutLd({
      url: "https://drhint.com/about-us",
      name: TITLE,
      description: DESC,
      trail: [
        { name: "Home", item: "https://drhint.com/" },
        { name: "About Us", item: "https://drhint.com/about-us" },
      ],
    }),
  }),
  component: AboutUsPage,
});

function AboutUsPage() {
  return (
    <Layout>
      <article className="mx-auto max-w-3xl px-4 py-10 text-sm leading-relaxed">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">About DrHint</h1>
        <p className="mt-2 text-xs text-muted-foreground">The Platform Explanation Hub</p>

        <p className="mt-6 text-muted-foreground">
          DrHint is a premium multi-utility Micro-SaaS platform engineered to
          democratize real-time automated data processing across three high-intent
          consumer verticals: <b>precious metals finance</b>, <b>local utility
          services</b>, and <b>loan / EMI optimization</b>. Our mission is to
          surface neutral, calculator-grade
          information for end users who would otherwise be funnelled through
          opaque jeweller markups, paywalled bill portals, or aggressive
          affiliate loan brokers.
        </p>

        <h2 className="mt-8 text-xl font-bold">Our corporate mission</h2>
        <p className="mt-2 text-muted-foreground">
          We build automated, public-data-backed calculators that anyone — a
          first-time gold buyer in Karachi, a household checking a LESCO bill
          in Lahore, or a family comparing a home-loan EMI in Mumbai — can use without
          registration, paywall, or sales pressure. Every tool is engineered as
          an independent, siloed module so users stay inside the lane they
          arrived for.
        </p>

        <h2 className="mt-8 text-xl font-bold">What we operate</h2>
        <ul className="mt-2 list-disc pl-5 text-muted-foreground space-y-1">
          <li><b>Precious Metals Ledger</b> — Live 24K / 22K / 21K gold and silver
            (chandi) rate calculators for Pakistan and India, refreshed every 10 minutes
            from international spot prices and live FX.</li>
          <li><b>Regional Utility Portals</b> — Independent bill-lookup utilities
            for LESCO, IESCO, MEPCO, GEPCO, PESCO, HESCO, SEPCO, QESCO,
            FESCO, K-Electric, SNGPL, SSGC, PTCL and KWSB.</li>
          <li><b>Debt Optimization Hub</b> — EMI and interest-saver payoff
            calculators across 30 banks in US, India and Pakistan.</li>
        </ul>

        <h2 className="mt-8 text-xl font-bold">Editorial independence</h2>
        <p className="mt-2 text-muted-foreground">
          DrHint is not a registered financial advisor, broker, dealer,
          jeweller, utility company, or government department. We do not earn
          commissions on metal trades or loan originations. Our revenue comes
          exclusively from contextually-served programmatic advertising
          (including Google AdSense), which is disclosed in our{" "}
          <Link to="/privacy-policy" className="text-primary underline">Privacy Policy</Link>{" "}
          and our{" "}
          <Link to="/disclaimer" className="text-primary underline">Disclaimer</Link>.
        </p>

        <h2 className="mt-8 text-xl font-bold">Contact</h2>
        <p className="mt-2 text-muted-foreground">
          General queries:{" "}
          <a href="mailto:attapops4@gmail.com" className="text-primary underline">attapops4@gmail.com</a>
          . Editorial corrections, partnerships and city-coverage requests are
          handled within 5 business days. See{" "}
          <Link to="/contact" className="text-primary underline">/contact</Link>.
        </p>
      </article>
    </Layout>
  );
}